import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCafeAuth } from "../auth/CafeAuthContext";
import api from "../lib/api";
import { CafeShell, Field, inputStyle } from "./CafeShell";
import { Copy, LogOut, ExternalLink } from "lucide-react";
import CafeQR from "../components/CafeQR";
import CreatorShareKit from "./CreatorShareKit";
import { generateTableTentPDF } from "../lib/tableTentPDF";

const TENT_VARIANTS = [
  {
    key: "classic",
    label: "// classic · direct",
    preview: '"Scan your cup. Get a reading. Three minutes. No download. Just your dregs and whatever they decided to tell you."',
  },
  {
    key: "poetic",
    label: "// poetic · pull-quote",
    preview: '"Your cup is empty. What remains is not." — leads with the line, no header.',
  },
  {
    key: "close",
    label: "// close · invitation",
    preview: '"Finish your drink. Then look closer." — the grounds hold echoes; the silt knows.',
  },
];

export default function CafeDashboardPage() {
  const { cafe, loading, logout, updateProfile } = useCafeAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [statsWindow, setStatsWindow] = useState("month"); // month | all

  const [noteDraft, setNoteDraft] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("venmo");
  const [payoutHandle, setPayoutHandle] = useState("");
  const [saveStatus, setSaveStatus] = useState(null);
  const [copied, setCopied] = useState(false);

  // Table-tent picker state
  const [pdfVariant, setPdfVariant] = useState("classic");
  const [pdfLight, setPdfLight] = useState(true); // default to printer-friendly

  useEffect(() => {
    if (!loading && cafe === false) navigate("/cafe/login", { replace: true });
  }, [loading, cafe, navigate]);

  useEffect(() => {
    if (cafe && typeof cafe === "object") {
      setNoteDraft(cafe.custom_note || "");
      setPayoutMethod(cafe.payout_method || "venmo");
      setPayoutHandle(cafe.payout_handle || "");
    }
  }, [cafe]);

  const [statsLastUpdated, setStatsLastUpdated] = useState(null);
  const [statsRefreshing, setStatsRefreshing] = useState(false);
  const statsLoadRef = useRef(null);

  useEffect(() => {
    if (!cafe || typeof cafe !== "object") return;
    let cancelled = false;
    const load = async () => {
      setStatsRefreshing(true);
      try {
        const { data } = await api.get("/cafes/me/stats", { params: { window: statsWindow } });
        if (!cancelled) {
          setStats(data);
          setStatsLastUpdated(new Date());
        }
      } catch (err) {
        if (!cancelled) setStatsError(err.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setStatsRefreshing(false);
      }
    };
    statsLoadRef.current = load;
    load();
    // 4× per day auto-refresh — every 6 hours
    const id = setInterval(load, 6 * 60 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); statsLoadRef.current = null; };
  }, [cafe, statsWindow]);

  if (loading || cafe === null) {
    return (
      <CafeShell testId="cafe-dashboard-loading">
        <p className="italic-quote" style={{ textAlign: "center", color: "var(--gold)" }}>steeping…</p>
      </CafeShell>
    );
  }
  if (!cafe || typeof cafe !== "object") return null;

  const cafeUrl = `${window.location.origin}/c/${cafe.slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cafeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const saveProfile = async () => {
    setSaveStatus("saving");
    try {
      await updateProfile({
        custom_note: noteDraft.trim() || null,
        payout_method: payoutMethod,
        payout_handle: payoutHandle.trim(),
      });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 1800);
    } catch (err) {
      setSaveStatus("error");
    }
  };

  const conversionRate = stats && stats.clicks > 0
    ? Math.round((stats.conversions / stats.clicks) * 100)
    : 0;

  return (
    <CafeShell testId="cafe-dashboard-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <h2 className="italic-quote" style={{ fontSize: 30, color: "var(--cream)", margin: 0 }} data-testid="dashboard-cafe-name">
          {cafe.name}
        </h2>
        <button
          onClick={logout}
          data-testid="dashboard-logout"
          style={{ background: "transparent", border: "none", color: "var(--terracotta-soft)", cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}
        >
          <LogOut size={14} /> sign out
        </button>
      </div>
      <p style={{ marginBottom: 32, fontSize: 12, color: "var(--muted)", fontFamily: "'Space Mono', monospace", letterSpacing: "0.06em" }}>
        // dashboard auto-refresh: every 15s
      </p>

      {/* LINK CARD — terminal style */}
      <div className="terminal-frame" style={{ marginBottom: 32 }} data-testid="dashboard-link-card">
        <div className="terminal-bar">
          <span>SOMA://YOUR_LINK</span>
          <span>● ● ●</span>
        </div>
        <div className="terminal-corners" style={{ padding: "20px 18px", position: "relative" }}>
          <span className="tc tc-tl">┌</span><span className="tc tc-tr">┐</span>
          <span className="tc tc-bl">└</span><span className="tc tc-br">┘</span>
          <code data-testid="dashboard-cafe-url" style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, color: "var(--cream)", wordBreak: "break-all", display: "block", textAlign: "center", padding: "10px 24px 16px" }}>
            {cafeUrl}
          </code>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", position: "relative", zIndex: 1 }}>
            <button onClick={copyLink} data-testid="dashboard-copy-btn" className="mono-btn">
              <Copy size={11} style={{ verticalAlign: "middle", marginRight: 6 }} /> {copied ? "COPIED" : "COPY"}
            </button>
            <a href={cafeUrl} target="_blank" rel="noopener noreferrer" data-testid="dashboard-open-link" className="mono-btn" style={{ textDecoration: "none" }}>
              <ExternalLink size={11} style={{ verticalAlign: "middle", marginRight: 6 }} /> OPEN
            </a>
          </div>
        </div>
      </div>

      {/* QR + PRINTABLE TABLE-TENT (cafes) or QR + SHARE KIT (creators) */}
      {cafe.partner_type === "creator" ? (
        <CreatorShareKit cafe={cafe} cafeUrl={cafeUrl} />
      ) : (
        <div style={{ marginBottom: 32 }} data-testid="dashboard-qr-card">
        <p className="section-marker" style={{ marginBottom: 12 }}>your table-tent</p>
        <p style={{ color: "var(--cream-soft)", fontStyle: "italic", fontFamily: "'IM Fell DW Pica', serif", fontSize: 15, margin: "0 0 14px", lineHeight: 1.5 }}>
          Print and put on the counter, next to the register, or on each table. Pick the voice that fits your crowd — copy is final, just download.
        </p>

        {/* MODE TOGGLE — light is default to save printer ink */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }} data-testid="pdf-mode-toggle">
          <button
            type="button"
            data-testid="pdf-mode-light"
            onClick={() => setPdfLight(true)}
            className={`mono-btn ${pdfLight ? "active" : ""}`}
            style={{ flex: 1 }}
          >
            light · ink-saver
          </button>
          <button
            type="button"
            data-testid="pdf-mode-dark"
            onClick={() => setPdfLight(false)}
            className={`mono-btn ${!pdfLight ? "active" : ""}`}
            style={{ flex: 1 }}
          >
            dark · brand-true
          </button>
        </div>

        {/* VARIANT CARDS */}
        <div style={{ display: "grid", gap: 8, marginBottom: 16 }} data-testid="pdf-variant-picker">
          {TENT_VARIANTS.map((v) => (
            <button
              key={v.key}
              type="button"
              data-testid={`pdf-variant-${v.key}`}
              onClick={() => setPdfVariant(v.key)}
              style={{
                textAlign: "left",
                padding: "12px 14px",
                background: pdfVariant === v.key ? "rgba(143, 196, 224, 0.10)" : "rgba(143, 196, 224, 0.03)",
                border: `1px solid ${pdfVariant === v.key ? "var(--terracotta-soft)" : "rgba(143, 196, 224, 0.22)"}`,
                color: "var(--cream)",
                fontFamily: "'IM Fell DW Pica', serif",
                cursor: "pointer",
                borderRadius: 0,
              }}
            >
              <span style={{ display: "block", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--terracotta-soft)", marginBottom: 4 }}>
                {v.label}
              </span>
              <span style={{ fontStyle: "italic", fontSize: 14, color: "var(--cream-soft)", lineHeight: 1.4 }}>
                {v.preview}
              </span>
            </button>
          ))}
        </div>

        {/* QR PREVIEW (reflects selected mode) */}
        <div style={{ display: "flex", justifyContent: "center", padding: "6px 0 4px" }}>
          <CafeQR
            url={cafeUrl}
            size={180}
            filename={`soma-lixir-${cafe.slug}-qr.png`}
            mode={pdfLight ? "light" : "dark"}
            testId="dashboard-qr"
            showDownloadBtn={false}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
          <button
            type="button"
            data-testid="dashboard-tent-pdf-btn"
            onClick={async () => {
              try {
                await generateTableTentPDF({
                  cafe,
                  qrCanvasSelector: '[data-testid="dashboard-qr"]',
                  landingOrigin: window.location.origin,
                  variant: pdfVariant,
                  lightMode: pdfLight,
                });
              } catch (err) {
                console.error("PDF generation failed:", err);
                alert("Couldn't generate the PDF. Try refreshing the page first.");
              }
            }}
            className="mono-btn"
            style={{ padding: "10px 18px" }}
          >
            ↓ DOWNLOAD TABLE-TENT (PDF)
          </button>
        </div>
        <p style={{ textAlign: "center", marginTop: 10, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 12, color: "var(--dim)", lineHeight: 1.4 }}>
          8.5×11" sheet · prints <strong>2 fold-and-cut table tents</strong> per page · light mode uses ~70% less ink
        </p>
      </div>
      )}

      {/* STATS */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 12, flexWrap: "wrap" }}>
        <p className="section-marker" style={{ margin: 0 }}>your numbers</p>
        <div style={{ display: "flex", gap: 6 }} data-testid="dashboard-window-toggle">
          {[
            { key: "month", label: "this.month" },
            { key: "all", label: "all.time" },
          ].map((opt) => (
            <button
              key={opt.key}
              data-testid={`window-${opt.key}`}
              onClick={() => setStatsWindow(opt.key)}
              className={`mono-btn ${statsWindow === opt.key ? "active" : ""}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <p style={{ margin: 0, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--dim)" }} data-testid="stats-last-updated">
          {statsLastUpdated
            ? `updated ${formatRelative(statsLastUpdated)} · auto-refresh 4× a day`
            : "updating…"}
        </p>
        <button
          type="button"
          data-testid="stats-refresh-btn"
          onClick={() => statsLoadRef.current?.()}
          disabled={statsRefreshing}
          style={{
            background: "transparent",
            border: "1px solid rgba(143, 196, 224, 0.42)",
            color: "var(--terracotta-soft)",
            padding: "6px 12px",
            fontFamily: "'Space Mono', monospace",
            fontSize: 10,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            cursor: statsRefreshing ? "wait" : "pointer",
            borderRadius: 0,
            opacity: statsRefreshing ? 0.5 : 1,
          }}
        >
          {statsRefreshing ? "refreshing…" : "↻ refresh now"}
        </button>
      </div>
      {statsError && <p className="error-banner" style={{ marginBottom: 18 }}>{statsError}</p>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 18 }} data-testid="dashboard-stats">
        <StatTile testId="stat-clicks" label="clicks" value={stats?.clicks ?? "—"} hint="opened your link" />
        <StatTile testId="stat-readings" label="readings.started" value={stats?.readings_started ?? "—"} hint="saw a teaser" />
        <StatTile testId="stat-conversions" label="paid.readings" value={stats?.conversions ?? "—"} hint={`${conversionRate}% conversion`} />
        <StatTile testId="stat-earned" label="you've.earned" value={stats ? `$${stats.earned_estimate.toFixed(2)}` : "—"} hint={stats ? `$${stats.cafe_share_per_reading.toFixed(2)} per reading` : ""} />
      </div>
      <p style={{ fontSize: 12, color: "var(--dim)", marginBottom: 32, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", lineHeight: 1.55, textAlign: "center" }}>
        Public price is $7.77 — your link auto-applies the partner discount to $5.55. You earn 44% per reading. Payouts go out every Friday.
      </p>

      {/* PROFILE — paper card */}
      <div className="paper-card" style={{ marginBottom: 24, position: "relative" }}>
        <span className="wax-seal">edit</span>
        <p className="paper-card-title">Your landing</p>
        <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 13, color: "var(--rust)", textAlign: "center", margin: "10px 0 16px" }}>
          The note shown on your custom cafe page.
        </p>
        <textarea
          data-testid="dashboard-note"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          style={{
            width: "100%",
            minHeight: 90,
            border: "1px solid rgba(33, 19, 12, 0.25)",
            background: "rgba(33, 19, 12, 0.04)",
            color: "var(--ink)",
            padding: 12,
            fontFamily: "'IM Fell DW Pica', serif",
            fontStyle: "italic",
            fontSize: 17,
            outline: "none",
            resize: "vertical",
            borderRadius: 0,
          }}
          maxLength={160}
          placeholder="From the team at Flora — enjoy a moment with us."
        />
        <p style={{ marginTop: 6, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "var(--rust)", letterSpacing: "0.1em", textAlign: "right" }}>
          {noteDraft.length}/160
        </p>
      </div>

      <p className="section-marker">payouts</p>
      <Field label="payout method">
        <div style={{ display: "flex", gap: 10 }}>
          {["venmo", "stripe"].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`dashboard-payout-${m}`}
              onClick={() => setPayoutMethod(m)}
              className="toggle-btn"
              style={{
                flex: 1,
                background: payoutMethod === m ? "var(--cream)" : "transparent",
                color: payoutMethod === m ? "var(--bg-1)" : "var(--cream-soft)",
                borderColor: payoutMethod === m ? "var(--cream)" : "rgba(143, 196, 224, 0.35)",
              }}
            >
              {m === "venmo" ? "Venmo" : "Stripe / Bank"}
            </button>
          ))}
        </div>
      </Field>
      <Field label={payoutMethod === "venmo" ? "venmo handle" : "stripe email / bank handle"}>
        <input
          data-testid="dashboard-payout-handle"
          type="text"
          value={payoutHandle}
          onChange={(e) => setPayoutHandle(e.target.value)}
          style={inputStyle}
        />
      </Field>

      <button data-testid="dashboard-save" className="primary-btn" onClick={saveProfile} disabled={saveStatus === "saving"}>
        {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved ✓" : saveStatus === "error" ? "Couldn't save" : "Save changes"}
      </button>
    </CafeShell>
  );
}

function StatTile({ label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="terminal-corners" style={{
      position: "relative",
      padding: 18,
      borderRadius: 0,
      background: "rgba(10, 6, 4, 0.7)",
      border: "1px solid rgba(143, 196, 224, 0.32)",
      overflow: "hidden",
    }}>
      <span className="tc tc-tl" style={{ fontSize: 14 }}>┌</span>
      <span className="tc tc-tr" style={{ fontSize: 14 }}>┐</span>
      <span className="tc tc-bl" style={{ fontSize: 14 }}>└</span>
      <span className="tc tc-br" style={{ fontSize: 14 }}>┘</span>
      <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", color: "var(--terracotta-soft)", margin: "0 0 10px", textTransform: "uppercase", textAlign: "center" }}>
        // {label}
      </p>
      <p style={{ fontFamily: "'VT323', monospace", fontSize: 38, color: "var(--cream)", margin: 0, lineHeight: 1, textAlign: "center" }}>
        {value}
      </p>
      {hint && (
        <p style={{ marginTop: 8, fontSize: 12, color: "var(--dim)", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", lineHeight: 1.3, textAlign: "center" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function formatRelative(date) {
  const diff = Math.max(0, (Date.now() - date.getTime()) / 1000); // seconds
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) {
    const hrs = Math.floor(diff / 3600);
    return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  }
  const days = Math.floor(diff / 86400);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

